import { useMutation } from '@apollo/client';
import { Grid } from '@mui/material';

import { Formik } from 'formik';
import { Gateway } from 'index';
import { omit, omitBy, pick } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import CREATE_MUTATION from 'gql/mutations/InternationalOffice/addIOCourse.gql';
import UPDATE_MUTATION from 'gql/mutations/InternationalOffice/updateIOCourse.gql';

import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { validateForm } from 'utils/FormErrorHelper';

import { CourseInformation } from './components/CourseInformation';
import { ioCourseValidationSchema } from './validation';

interface Props {
  closeHandler: Function;
  setDisabledCancel?: Function;
  course?: any;
}

export function IOCourse(props: Props) {
  const [submitting, setSubmitting] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [createIOCourse] = useMutation(CREATE_MUTATION);
  const [updateIOCourse] = useMutation(UPDATE_MUTATION);

  const initialValues = () => {
    return props.course
      ? {
          ...pick(props.course, [
            'id',
            'orderNumber',
            'courseNumber',
            'courseName',
            'courseOfficer',
            'degree',
            'ectCount',
            'faculty',
            'isWinter',
            'isSummer',
            'isAWPM',
            'courseDescription'
          ])
        }
      : {
          isWinter: false,
          isSummer: false,
          isAWPM: false
        };
  };

  const handleSaveClick = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }
    const omitFields = ['id'];
    let saveBody: any = {
      ...omit(values, omitFields),
      orderNumber: parseInt(values.orderNumber, 10),
      ectCount: parseInt(values.ectCount, 10)
    };

    if (
      values.courseDescription !== undefined &&
      props.course?.courseDescription !== values.courseDescription
    ) {
      if (values.courseDescription === null) {
        saveBody.courseDescription = null;
      } else {
        const formData = new FormData();
        formData.append('file', values.courseDescription);
        try {
          const { data: fileURI } = await Gateway.post('/upload/io/files/courses', formData);
          saveBody.courseDescription = fileURI;
        } catch (err) {
          dispatch(
            AlertActions.error(
              t(
                translations.pages.internationalOffice.courses.alerts.error.savingCourseDescription()
              )
            )
          );
          props.closeHandler();
          setSubmitting(false);
          return;
        }
      }
    }

    if (values.id) {
      // * UPDATE IO-COURSE
      try {
        await updateIOCourse({
          variables: {
            id: values.id,
            data: {
              ...omitBy(saveBody, v => v === null || v === ''),
              courseDescription: saveBody.courseDescription
            }
          },
          refetchQueries: ['GetIOCourses']
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.internationalOffice.courses.alerts.success.updatedCourse())
          )
        );
        setSubmitting(false);
        props.closeHandler();
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(translations.pages.internationalOffice.courses.alerts.error.updatingCourse())
          )
        );
        setSubmitting(false);
      }
    } else {
      // * CREATE IO-COURSE
      try {
        await createIOCourse({
          variables: { data: omitBy(saveBody, v => v === null || v === '') },
          refetchQueries: ['GetIOCourses']
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.internationalOffice.courses.alerts.success.createdCourse())
          )
        );
        setSubmitting(false);
        props.closeHandler();
      } catch (err: any) {
        if (err?.graphQLErrors[0]?.extensions?.response) {
          dispatch(
            AlertActions.error(err?.graphQLErrors[0]?.extensions?.response?.body?.status?.message)
          );
        } else {
          dispatch(
            AlertActions.error(
              t(translations.pages.internationalOffice.courses.alerts.error.creatingCourse())
            )
          );
        }
        setSubmitting(false);
      }
    }
    if (props.setDisabledCancel) {
      props.setDisabledCancel(false);
    }
  };

  const steps = ['Course Creation'];

  const CourseModalStep = ({ step }) => {
    switch (step) {
      case 0:
        return <CourseInformation />;
      default:
        return null;
    }
  };

  return (
    <Grid item className="ioCourseModal">
      <Formik
        isInitialValid={!!props.course}
        initialValues={initialValues()}
        onSubmit={values => {
          handleSaveClick(values);
        }}
        validationSchema={ioCourseValidationSchema(t)}
        validate={values => {
          return validateForm(values, ioCourseValidationSchema(t), dispatch);
        }}
      >
        <MultistepForm
          submitting={submitting}
          steps={steps}
          onCancel={props.closeHandler}
          hideStepper
        >
          {activeStep => (
            <Grid container>
              <CourseModalStep step={activeStep} />
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
}
